import Api, { handleError } from '@/services/Api';
import store from '@/store';
import $ from 'jquery';

const API_URL = process.env.API_BASE;
const BASE = `${API_URL}/ai_group_interview`;

export function resolveAiGiDownloadFilename(headers = {}, fallback = 'download.bin') {
  const disposition = headers['content-disposition'] || headers['Content-Disposition'];
  if (!disposition || typeof disposition !== 'string') {
    return fallback;
  }

  const starMatch = disposition.match(/filename\*=([\w.-]+)'([^']*)'([^;]+)/i);
  if (starMatch && /^utf-8$/i.test(starMatch[1])) {
    try {
      const decoded = decodeURIComponent(starMatch[3].trim());
      if (decoded) {
        return decoded;
      }
    } catch (_) {
      // fall through to plain filename parsing
    }
  }

  const quotedMatch = disposition.match(/filename="([^"]+)"/);
  if (quotedMatch && quotedMatch[1]) {
    return quotedMatch[1];
  }

  const plainMatch = disposition.match(/filename=([^;\s]+)/);
  if (plainMatch && plainMatch[1]) {
    return plainMatch[1].replace(/^["']|["']$/g, '');
  }

  return fallback;
}

export function downloadAiGiBlob(blob, filename) {
  if (!blob) {
    return;
  }
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename);
    return;
  }
  const fileURL = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = fileURL;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(fileURL);
}

export async function getAiGiUsageSummaryApi() {
  const response = await Api.get(`${BASE}/usage_summary/`).catch(handleError);
  if (!response) {
    return { status: 0, data: null };
  }
  return { status: response.status, data: response.data };
}

export async function getAiGiPastProjectsApi() {
  const response = await Api.get(`${BASE}/past_projects/`).catch(handleError);
  if (!response) {
    return { status: 0, data: null };
  }
  return { status: response.status, data: response.data };
}

export async function getOldProjectListAiGroupInterviewApi(payload) {
  const url = `${API_URL}/old_project_list_ai_group_interview/`;
  const response = await Api.get(url, {
    params: payload,
    paramsSerializer: params => $.param(params),
  }).catch(handleError);
  if (!response) {
    return { status: 0, data: null };
  }
  return { status: response.status, data: response.data };
}

export async function createAiGiProjectApi(payload) {
  const response = await Api.post(`${BASE}/projects/`, payload).catch(handleError);
  if (!response) {
    return { status: 0, data: null };
  }
  return { status: response.status, data: response.data };
}

export async function getAiGiProjectApi(projectId) {
  const response = await Api.get(`${BASE}/projects/${projectId}/`).catch(handleError);
  if (!response) {
    return { status: 0, data: null };
  }
  return { status: response.status, data: response.data };
}

export async function updateAiGiFormApi(projectId, payload) {
  const response = await Api.patch(`${BASE}/projects/${projectId}/`, payload).catch(handleError);
  if (!response) {
    return { status: 0, data: null };
  }
  return { status: response.status, data: response.data };
}

export async function validateAiGiFormApi(payload) {
  const response = await Api.post(`${BASE}/validate_form/`, payload).catch(handleError);
  if (!response) {
    return { status: 0, data: null };
  }
  return { status: response.status, data: response.data };
}

export async function startAiGiResearchApi(projectId) {
  const response = await Api.post(`${BASE}/projects/${projectId}/start/`).catch(handleError);
  if (!response) {
    return { status: 0, data: null };
  }
  return { status: response.status, data: response.data };
}

export async function getAiGiProgressApi(projectId) {
  const response = await Api.get(`${BASE}/projects/${projectId}/progress/`).catch(handleError);
  if (!response) {
    return { status: 0, data: null };
  }
  return { status: response.status, data: response.data };
}

export async function getAiGiResultApi(projectId) {
  try {
    const response = await Api.get(`${BASE}/projects/${projectId}/result/`);
    return { status: response.status, data: response.data };
  } catch (error) {
    const response = error && error.response ? error.response : null;
    // 403 = no AI GI permission / not allowed; keep session, let ResearchResult handle it.
    if (response && response.status === 403) {
      return { status: response.status, data: response.data };
    }
    const handled = handleError(error);
    if (!handled) {
      return { status: 0, data: null };
    }
    return { status: handled.status, data: handled.data };
  }
}

function isJsonDownloadPayload(headers, data) {
  const contentType = String(
    (headers && (headers['content-type'] || headers['Content-Type']))
    || (data && data.type)
    || '',
  ).toLowerCase();
  return contentType.includes('application/json') || contentType.includes('text/json');
}

async function parseAiGiDownloadError(data) {
  if (!data) {
    return null;
  }
  if (typeof data === 'object' && typeof data.text !== 'function') {
    return data;
  }
  try {
    const text = await data.text();
    if (!text) {
      return null;
    }
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
}

async function normalizeAiGiDownloadResponse(response) {
  if (!response) {
    return { status: 0, data: null, headers: {} };
  }
  const headers = response.headers || {};
  const status = response.status;
  const data = response.data;
  if (status >= 200 && status < 300 && data && !isJsonDownloadPayload(headers, data)) {
    return { status, data, headers };
  }
  const parsed = await parseAiGiDownloadError(data);
  return { status, data: parsed, headers };
}

export async function downloadAiGiUtterancesApi(projectId, format = 'txt') {
  const response = await Api.get(`${BASE}/projects/${projectId}/download/utterances/`, {
    params: { export: format },
    responseType: 'blob',
  }).catch(handleError);
  return normalizeAiGiDownloadResponse(response);
}

export async function downloadAiGiReportsApi(projectId, format) {
  const response = await Api.get(`${BASE}/projects/${projectId}/download/reports/`, {
    params: { export: format },
    responseType: 'blob',
  }).catch(handleError);
  return normalizeAiGiDownloadResponse(response);
}

export async function suggestAiGiQuestionsApi(payload) {
  const response = await Api.post(
    `${BASE}/suggest_questions/`,
    payload,
  ).catch(handleError);
  if (!response) {
    return { status: 0, data: null };
  }
  return { status: response.status, data: response.data };
}

export async function getResearchAvatarConfig() {
  const url = `${BASE}/research-avatar/config/`;
  const response = await Api.get(url).catch(handleError);
  if (!response) {
    return { ok: false, status: 0, data: null };
  }
  const { status, data } = response;
  return { ok: status === 200, status, data };
}

export async function createResearchAvatarRealtimeCall(sdp, { voice } = {}) {
  const params = new URLSearchParams();
  if (voice) params.set('voice', voice);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const url = `${BASE}/research-avatar/realtime/calls/${suffix}`;
  const token = localStorage.getItem('token');
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/sdp',
      Authorization: `JWT ${token}`,
      'Accept-Language': store.getters.getAccessLanguage,
    },
    body: sdp,
  }).catch(handleError);
  if (!response) {
    return { ok: false, status: 0, text: '' };
  }
  const text = await response.text().catch(() => '');
  return { ok: response.ok, status: response.status, text };
}

export async function finalizeResearchAvatar(messages) {
  const url = `${BASE}/research-avatar/finalize/`;
  const response = await Api.post(url, { messages }).catch(handleError);
  if (!response) {
    return { ok: false, status: 0, data: null };
  }
  const { status, data } = response;
  return { ok: status === 200, status, data };
}
